'use client'

import * as SelectPrimitive from '@radix-ui/react-select'
import { cva, VariantProps } from 'class-variance-authority'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { ComponentProps, createContext, CSSProperties, useContext } from 'react'

import { cn } from '@/shared/lib'

import { Icon } from '../icon'

type SelectState = {
  mode: 'select' | 'menu'
  size: 'default' | 'small'
  width: number
  readOnly: boolean
}

const SelectContext = createContext<SelectState | null>(null)

const useSelectContext = () => {
  const state = useContext(SelectContext)

  if (!state) throw new Error('useSelectContext must be used within a Select')

  return state
}

type RootProps = Omit<
  ComponentProps<typeof SelectPrimitive.Root>,
  'onChange'
> & {
  mode?: 'select' | 'menu'
  size?: 'default' | 'small'
  width?: number
  readOnly?: boolean
  'aria-readonly'?: boolean
  'aria-disabled'?: boolean
  'aria-invalid'?: boolean
  onChange?: (value: string) => void
}

export const Root = ({
  mode = 'select',
  size = 'default',
  width = 180,
  readOnly: readOnlyProp = false,
  disabled: disabledProp = false,
  onValueChange,
  onChange,
  ...restProps
}: RootProps) => {
  const readOnly = readOnlyProp || Boolean(restProps['aria-readonly'])
  const disabled = disabledProp || Boolean(restProps['aria-disabled'])

  return (
    <SelectContext.Provider value={{ mode, size, width, readOnly }}>
      <SelectPrimitive.Root
        data-slot="select"
        onValueChange={onValueChange ?? onChange}
        disabled={disabled}
        {...(readOnly && { open: false })}
        {...restProps}
      />
    </SelectContext.Provider>
  )
}

export const Group = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Group>) => {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn('flex flex-col', className)}
      {...props}
    />
  )
}

export const Value = ({
  ...props
}: ComponentProps<typeof SelectPrimitive.Value>) => {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

const triggerVariants = cva(
  [
    'typo-label1 flex cursor-pointer items-center transition-[color] outline-none',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200 [&[data-state=open]_svg]:rotate-180',
    '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:block *:data-[slot=select-value]:truncate',
  ],
  {
    variants: {
      mode: {
        select: 'data-[placeholder]:text-gray-400',
        menu: 'font-medium data-[placeholder]:text-black',
      },
      size: {
        default: '',
        small: '',
      },
      variant: {
        text: [
          'h-fit justify-end gap-1 rounded-sm',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        ],
        box: [
          'justify-between gap-1 rounded-sm border border-gray-100 bg-white',
          'focus:border-black focus-visible:border-black focus-visible:outline-none',
          'disabled:cursor-default disabled:bg-gray-50 disabled:text-gray-400',
          'aria-invalid:border-red-500',
        ],
      },
      readOnly: {
        true: 'pointer-events-none cursor-default bg-gray-50',
        false: '',
      },
    },

    compoundVariants: [
      {
        mode: 'select',
        variant: 'box',
        className: 'h-12 px-[11px] py-[13px]',
      },
      {
        mode: 'menu',
        size: 'default',
        variant: 'box',
        className: 'h-9 px-[11px] py-[7px]',
      },
      {
        mode: 'menu',
        size: 'small',
        variant: 'box',
        className: 'h-8 px-[11px] py-[7px]',
      },
      {
        mode: 'menu',
        size: 'small',
        className: 'typo-caption1',
      },
    ],
  },
)

type TriggerProps = Omit<
  ComponentProps<typeof SelectPrimitive.Trigger>,
  'type'
> &
  VariantProps<typeof triggerVariants>

export const Trigger = ({
  variant = 'box',
  className,
  children,
  style,
  ...props
}: TriggerProps) => {
  const { mode, size, width, readOnly } = useSelectContext()

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        'w-[var(--select-width)]',
        triggerVariants({ mode, size, variant, readOnly, className }),
      )}
      style={
        {
          '--select-width': `${width}px`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <Icon
          name="arrow-bottom"
          strokeWidth={mode === 'menu' ? 1 : 1.5}
          className={cn(
            mode === 'menu' ? 'size-4 text-black' : 'size-[18px] text-gray-500',
          )}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

const contentVariants = cva(
  [
    'group typo-label1 z-popover relative max-h-(--radix-select-content-available-height) min-w-20 overflow-x-hidden overflow-y-auto rounded-sm border border-gray-100 bg-white text-gray-400',
    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
    'data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2',
    'data-[side=top]:slide-in-from-bottom-2',
  ],
  {
    variants: {
      shadow: {
        true: 'shadow-[0px_0px_7px_0px_rgba(0,0,0,0.25)]',
        false: '',
      },
    },
  },
)

const viewportVariants = cva('flex flex-col py-[13px]', {
  variants: {
    variant: {
      text: [
        'gap-3',
        '[&_[data-slot=select-group]]:gap-3',
        '[&_[data-slot=select-item]]:focus:text-black',
        '[&_[data-slot=select-item][data-state=checked]]:text-black',
      ],
      box: [
        '[&_[data-slot=select-item]]:h-9',
        '[&_[data-slot=select-item]]:focus:bg-gray-50',
        '[&_[data-slot=select-item]]:focus:text-black',
        '[&_[data-slot=select-item][data-state=checked]]:bg-gray-50',
        '[&_[data-slot=select-item][data-state=checked]]:text-black',
        '[&_[data-slot=select-item][data-state=checked]]:font-medium',
      ],
    },
    mode: {
      select: '',
      menu: '',
    },
    shadow: {
      true: '',
      false: '',
    },
    size: {
      default: '',
      small: '',
    },
  },

  compoundVariants: [
    {
      mode: 'menu',
      shadow: true,
      size: 'default',
      className: 'py-4',
    },
    {
      mode: 'menu',
      shadow: true,
      size: 'small',
      className: 'py-3',
    },
  ],
})

type ContentProps = ComponentProps<typeof SelectPrimitive.Content> &
  VariantProps<typeof viewportVariants> &
  VariantProps<typeof contentVariants>

export const Content = ({
  variant = 'box',
  shadow = false,
  sideOffset = 4,
  position = 'popper',
  className,
  children,
  ...props
}: ContentProps) => {
  const { mode, size, width } = useSelectContext()

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-shadow={shadow}
        className={cn(
          contentVariants({ shadow }),
          mode === 'menu' && variant === 'text' && 'rounded-lg',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        sideOffset={sideOffset}
        position={position}
        {...(mode === 'menu' && { style: { width: `${width}px` } })}
        {...props}
      >
        <ScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            viewportVariants({ mode, variant, shadow, size }),
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <ScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

export const Label = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Label>) => {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-sm font-medium', className)}
      {...props}
    />
  )
}

export const Item = ({
  className,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Item>) => {
  const { mode, size } = useSelectContext()

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'typo-label1 relative flex w-full cursor-pointer items-center gap-2 px-[11px] outline-hidden select-none',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        '*:[span]:last:block *:[span]:last:items-center *:[span]:last:gap-2 *:[span]:last:truncate',
        size === 'small' && 'typo-caption1',
        mode === 'menu' &&
          size === 'default' &&
          'group-data-[shadow=true]:px-4',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="block truncate">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

export const Separator = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.Separator>) => {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

export const ScrollUpButton = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

export const ScrollDownButton = ({
  className,
  ...props
}: ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}
